/**
 * 배달 플랫폼 표 — **플랫폼에 관한 사실을 선언하는 유일한 자리** (SSOT). 의존성 없는 순수 모듈이다.
 *
 * 플랫폼 목록·표시명·술어는 전부 아래 표 한 장에서 **파생**한다. 화면·유틸마다 플랫폼을 열거하면 플랫폼이 하나
 * 늘 때 사본마다 고쳐야 하고, 빠진 사본은 그 플랫폼을 「기타」로 접거나 원시 코드(`DDANGYO`)로 그리거나 선택지에서
 * 빼 버린다 — 2026-10-01 땡겨요(규약 §16.307)가 넷째 플랫폼이 됐을 때 네 파일 여섯 곳(표시명·매출 필터·그룹 접두·
 * 메뉴 동기화 대상·포장 축·옵션 안내 라벨)에 흩어진 3사 열거가 정확히 그랬다. 새 플랫폼은 **행 하나**로 들어온다.
 *
 * ★ **아무것도 import 하지 않는다.** `deliveryMenuNotes.js`·`posDeliverySoldout.js` 처럼 앱 번들 밖(`node --test`·
 * cypress 전처리기)에서 그대로 읽힌다 — `@/` 별칭이나 Vue 를 끌어오면 그 모듈들의 단위 시험이 함께 죽는다.
 *
 * 서버 쪽 같은 사실의 소유자:
 * - 그룹 코드·앱 코드 접기 — `DeliverySettlementMapper` 의 앱 코드 표(→ `platformGroup`, `delivery_app_tp` →
 *   `platform_display_group`). 통합 매출의 1순위 그룹 SQL 과 필터 정규화(`IntegratedSalesService.normalizePlatformFilter`)도
 *   같은 표에서 파생한다(2026-10-01, appserver `eff4b14e`·`7098fde0`)
 * - 메뉴 동기화 대상 — `DeliveryPlatformGroup.ALL`(배달 메뉴 조회 기본 범위)
 */

/**
 * 행 = 그룹 하나. **행의 순서가 화면의 표시 순서다**(필터·선택지·칩 정렬).
 *
 * - `code` — 그룹 코드. 서버 `platform_display_group`·`platform_group` 과 같은 문자열
 * - `label` — 사용자용 한글 표시명
 * - `codePrefix` — 이 그룹으로 접히는 앱 코드의 접두(`BAEMIN_APP`·`BAEMIN_ONE_APP` → 배민 등). 서버는 같은 사실을
 *   정확 코드 표로 갖는다 — 실재하는 앱 코드(`sys_code` `DELIVERY_APP_TP`)에서는 두 판정이 같다. 없는 행(오더톡·기타)은
 *   **그룹 코드 그대로**만 맞는다
 * - `menuSync` — 배달 메뉴 동기화 대상(서버 `DeliveryPlatformGroup.ALL`)
 * - `pickupChannel` — 같은 메뉴에 **포장 가격이 따로** 있다(§16.171). 배민은 `pickupMenuPrice`, 땡겨요는 가게의
 *   포장 서비스 가격(§16.307 — 포장 서비스가 켜진 가게에서만 관측된다). 요기요·쿠팡이츠는 그 필드 자체가 없다
 * - `salesFilter` — 배달 매출·정산 화면의 플랫폼 필터에 나온다(기타는 필터 대상이 아니다)
 */
const PLATFORM_ROWS = Object.freeze(
  [
    {
      code: 'BAEMIN',
      label: '배민',
      codePrefix: 'BAEMIN',
      menuSync: true,
      pickupChannel: true,
      salesFilter: true,
    },
    {
      code: 'YOGIYO',
      label: '요기요',
      codePrefix: 'YOGIYO',
      menuSync: true,
      pickupChannel: false,
      salesFilter: true,
    },
    {
      code: 'COUPANG_EATS',
      label: '쿠팡이츠',
      codePrefix: 'COUPANG',
      menuSync: true,
      pickupChannel: false,
      salesFilter: true,
    },
    {
      code: 'DDANGYO',
      label: '땡겨요',
      codePrefix: 'DDANGYO',
      menuSync: true,
      pickupChannel: true,
      salesFilter: true,
    },
    { code: 'ORDERTALK', label: '오더톡', codePrefix: null, menuSync: false, pickupChannel: false, salesFilter: true },
    { code: 'ETC', label: '기타', codePrefix: null, menuSync: false, pickupChannel: false, salesFilter: false },
  ].map((row) => Object.freeze(row))
)

const ETC = 'ETC'

function rowOf(group) {
  return PLATFORM_ROWS.find((row) => row.code === group) || null
}

const codesWhere = (flag) => Object.freeze(PLATFORM_ROWS.filter((row) => row[flag]).map((row) => row.code))

/**
 * 앱 코드·그룹 코드 → 그룹 코드. 접두가 맞는 행이 있으면 그 그룹, 없으면 **값 그대로**다
 * (`ORDERTALK` 은 그대로 오더톡이고, 모르는 코드는 호출부가 판단하도록 원문을 돌려준다).
 */
export function platformGroup(platform) {
  const value = String(platform || '')
  const row = PLATFORM_ROWS.find((candidate) => candidate.codePrefix && value.startsWith(candidate.codePrefix))
  return row ? row.code : value
}

/**
 * 코드값(`BAEMIN` · `BAEMIN_APP` · `BAEMIN_ONE_APP` · `COUPANG_APP` · `YOGIYO_EXPRESS_APP` · `DDANGYO_APP` · `ORDERTALK` …)
 * → 표에 있는 그룹 코드. 표에 없으면 `ETC`.
 */
export function deliveryAppGroupKey(code) {
  if (!code) return ETC
  const group = platformGroup(code)
  return rowOf(group) ? group : ETC
}

/** 코드값 → 사용자용 한글 표시명. 표에 없는 코드는 원문을 돌려준다(진단 정보다) */
export function platformDisplayName(code) {
  if (!code) return rowOf(ETC).label
  const row = rowOf(platformGroup(code))
  return row ? row.label : String(code)
}

/** 배달 매출 화면 플랫폼 필터 옵션(표시 순서). 라벨은 [platformDisplayName] 으로 파생 — 라벨 하드코딩 금지 */
export const DELIVERY_PLATFORM_FILTERS = codesWhere('salesFilter')

/** 수수료 매핑 등 ETC(기타)까지 포함해야 하는 화면용 — 표의 모든 그룹 */
export const DELIVERY_PLATFORM_GROUPS = Object.freeze(PLATFORM_ROWS.map((row) => row.code))

/**
 * 배달 메뉴 동기화 계약이 다루는 플랫폼(표시 순서) — 서버 `DeliveryPlatformGroup.ALL` 과 같은 집합이다.
 * 메뉴·옵션 상태 응답은 이 플랫폼마다 한 칸을 내린다(링크가 없으면 `linkState: null`).
 */
export const DELIVERY_MENU_PLATFORMS = codesWhere('menuSync')

/**
 * **그 플랫폼에 포장 축이 있는가** (§16.171·§16.177·§16.307).
 *
 * 포장 축이 없는 플랫폼(요기요·쿠팡이츠)은 그 필드 자체가 없어 관측하지 않으므로, 값을 넣어도 링크에 남을 뿐
 * **어디에도 비교되지 않는다** — 서버는 플랫폼으로 분기하지 않으니(데이터 모델에서 따라 나온다) 입력칸을 여는
 * 판단이 화면 몫이다. 그 판단을 화면마다 적으면 어떤 화면은 요기요에 포장칸을 열어 사장님이 "저장했는데 안
 * 나간다"를 겪는다.
 */
export function platformHasPickupChannel(platform) {
  const row = rowOf(platform)
  return !!row && row.pickupChannel
}
